import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { processRequest, RequestStatus } from './network/managedRequest';
import { API } from '../constants';
import { setSettingsLanguage } from './genericSettingsSlice';
import { ReactText } from 'react';
import { PbDispatch, PbGetState } from './index';
import withLocalStorage from '../util/withLocalStorage';
import { PbRequest } from './pbRequest';

const { setSelectedAccountingArea, getSelectedAccountingArea } =
    withLocalStorage();

export enum PartnerManagerDetailsType {
    EXTERNAL = 'EXTERNAL',
    EXTERNAL_WWB = 'EXTERNAL_WWB',
    ADMIN = 'ADMIN',
    ADMIN_READ_ONLY = 'ADMIN_READ_ONLY',
}

export enum PartnerManagerControl {
    ADMIN = 'ADMIN',
    NORMAL = 'NORMAL',
}

export interface PartnerManagerDetails {
    type: PartnerManagerDetailsType;
    control: PartnerManagerControl;
    lang: string;
}

export interface ManagedPartnerDto {
    id?: number;
    external_id: string;
    customer_id: number;
    company_name: string;
    credit_note_recipient: string;
    name?: string;
    phone?: string;
    country_id: number;
    currency_code: string;
    street_and_housenumber: string;
    postalcode: string;
    city: string;
    vip_costs_enabled: boolean;
    price_suggestion_available: boolean;
    partner_invoices_available: boolean;
    feature_access?: FeatureAccess[];
}

export const FEATURE_NAMES = [
    'MASTER_CONTRACT_DATA',
    'MASTER_SETTLEMENT_DETAILS',
    'MASTER_CREDIT_NOTE_RECIPIENT',
    'RESIDENTIAL_MANSION',
    'RESIDENTIAL_LOCATION',
    'RESIDENTIAL_APPLIANCE',
    'ACCOUNTING_NUMBERS',
    'ACCOUNTING_CREDIT_NOTES',
    'ACCOUNTING_PARTNER_INVOICES',
    'GENERAL_ACCESS_MANAGEMENT',
    'GENERAL_OPERATIONAL_CONTACTS',
] as const;

export type PartnerBoardFeature = (typeof FEATURE_NAMES)[number];
export type PartnerBoardFeatureRight = 'NONE' | 'READ_ONLY' | 'FULL_ACCESS';

export interface FeatureAccess {
    feature: PartnerBoardFeature;
    rights: PartnerBoardFeatureRight;
}

export interface ManagedPartnerWithLocationDto extends ManagedPartnerDto {
    location_id?: number;
    mansion_id?: number;
}

interface PartnersResult {
    partners: ManagedPartnerDto[];
}

interface PartnerManagerStore {
    partnerManagerDetails: PartnerManagerDetails | undefined;
    getPartnersResult: PartnersResult | undefined;
    specificPartner: ManagedPartnerDto | undefined;
    partnerByLocationId: ManagedPartnerWithLocationDto | undefined;
    selectedPartnerId?: string;
    selectedPartner?: ManagedPartnerDto;
    isAdminAccountManager: boolean;
    isReadOnlyAccountManager: boolean;
    canCreateAccountingArea: boolean;
    isCreatePartnerPrepared: boolean;
}

const initialState: PartnerManagerStore = {
    partnerManagerDetails: undefined,
    getPartnersResult: undefined,
    specificPartner: undefined,
    partnerByLocationId: undefined,
    selectedPartnerId: undefined,
    selectedPartner: undefined,
    isAdminAccountManager: false,
    isReadOnlyAccountManager: false,
    canCreateAccountingArea: false,
    isCreatePartnerPrepared: false,
};

export function getPartnerManagerPreload(): PartnerManagerStore {
    const storedSelectedId = getSelectedAccountingArea();
    return {
        ...initialState,
        selectedPartnerId: storedSelectedId,
    };
}

const partnerManagerSlice = createSlice({
    name: 'partnerManager',
    initialState,
    reducers: {
        setPartnerManagerDetails(
            state: PartnerManagerStore,
            action: PayloadAction<PartnerManagerDetails>
        ) {
            state.partnerManagerDetails = action.payload;
        },
        setGetPartnersResult(
            state: PartnerManagerStore,
            action: PayloadAction<PartnersResult>
        ) {
            state.getPartnersResult = action.payload;
        },
        setSpecificPartner(
            state: PartnerManagerStore,
            action: PayloadAction<ManagedPartnerDto>
        ) {
            state.specificPartner = action.payload;
        },
        setCreatePartnerPrepared(state, action) {
            state.isCreatePartnerPrepared = action.payload;
        },
        setPartnerByLocationId(
            state: PartnerManagerStore,
            action: PayloadAction<ManagedPartnerWithLocationDto>
        ) {
            state.partnerByLocationId = action.payload;
        },
        setSelectedPartnerId(state, action) {
            state.selectedPartnerId = action.payload;
        },
        setSelectedPartner(state, action) {
            state.selectedPartner = { ...action.payload };
        },
        setIsAdminAccountManager(state, action) {
            state.isAdminAccountManager = action.payload;
        },
        setIsReadOnlyAccountManager(state, action) {
            state.isReadOnlyAccountManager = action.payload;
        },
        setCanCreateAccountingArea(state, action) {
            state.canCreateAccountingArea = action.payload;
        },
    },
});

const {
    setPartnerManagerDetails,
    setSelectedPartnerId,
    setSelectedPartner,
    setGetPartnersResult,
    setSpecificPartner,
    setIsAdminAccountManager,
    setIsReadOnlyAccountManager,
    setPartnerByLocationId,
} = partnerManagerSlice.actions;

export function LoadPartnerManagerDetails() {
    return async (dispatch: PbDispatch) => {
        const partnerManagerDetails =
            await processRequest<PartnerManagerDetails>(
                dispatch,
                `${API}/partner_managers/me`,
                {},
                PbRequest.GET_PARTNER_MANAGER_DETAILS
            );
        dispatch(setPartnerManagerDetails(partnerManagerDetails));

        if (partnerManagerDetails.type === PartnerManagerDetailsType.ADMIN) {
            dispatch(setIsAdminAccountManager(true));
        } else if (
            partnerManagerDetails.type ===
            PartnerManagerDetailsType.ADMIN_READ_ONLY
        ) {
            dispatch(setIsReadOnlyAccountManager(true));
        }
        const canCreateAccountingArea =
            partnerManagerDetails.type === PartnerManagerDetailsType.ADMIN ||
            partnerManagerDetails.type ===
                PartnerManagerDetailsType.EXTERNAL_WWB;
        dispatch(
            partnerManagerSlice.actions.setCanCreateAccountingArea(
                canCreateAccountingArea
            )
        );

        dispatch(setSettingsLanguage(partnerManagerDetails.lang));
    };
}

export function LoadPartners() {
    return async (dispatch: PbDispatch, getState: PbGetState) => {
        try {
            const result = await processRequest<PartnersResult>(
                dispatch,
                `${API}/partners`,
                {},
                PbRequest.GET_PARTNERS
            );
            dispatch(setGetPartnersResult(result));

            let selectedPartnerId = getState().partnerManager.selectedPartnerId;

            if (result.partners.length > 0) {
                const noPartnerId = !selectedPartnerId;
                const foundPartner = result.partners.find(
                    (it) => it.external_id === selectedPartnerId
                );
                if (foundPartner && !noPartnerId) {
                    selectedPartnerId = foundPartner.external_id;
                } else {
                    dispatch(
                        setSelectedPartnerId(result.partners[0].external_id)
                    );

                    setSelectedAccountingArea(result.partners[0].external_id);

                    selectedPartnerId = result.partners[0].external_id;
                }
            }

            if (selectedPartnerId) {
                // this ensures partner specific data is loaded if we are directed to dashboard immediately
                // -> dashboard.tsx doesn't understand how to load all its required data
                await LoadSpecificPartner(selectedPartnerId)(
                    dispatch,
                    getState
                );
            }
        } catch {}
    };
}

export function UnloadPartner() {
    return async (dispatch: PbDispatch) => {
        setSelectedAccountingArea(null);
        dispatch(setSelectedPartnerId(undefined));
        dispatch(setSelectedPartner(undefined));
    };
}

export function LoadSpecificPartner(externalId: string) {
    return async (dispatch: PbDispatch, getState: PbGetState) => {
        try {
            const partner = await processRequest<ManagedPartnerDto>(
                dispatch,
                `${API}/users/${externalId}`,
                {},
                PbRequest.GET_SPECIFIC_PARTNER
            );
            dispatch(setSpecificPartner(partner));

            const selectedPartnerId =
                getState().partnerManager.selectedPartnerId;
            if (partner.external_id === selectedPartnerId) {
                dispatch(setSelectedPartner(partner));
            }
        } catch {}
    };
}

export function ReloadSpecificPartner() {
    return async (dispatch: PbDispatch, getState: PbGetState) => {
        const selectedPartnerId = getState().partnerManager.selectedPartnerId;
        if (selectedPartnerId) {
            await LoadSpecificPartner(selectedPartnerId)(dispatch, getState);
        }
    };
}

export function SetPartnerManagerLanguage(language: string) {
    return async (dispatch: PbDispatch) => {
        try {
            const request = {
                method: 'PATCH',
                body: JSON.stringify({ lang: language }),
            };
            await processRequest<PartnerManagerDetails>(
                dispatch,
                `${API}/partner_managers/me`,
                request,
                PbRequest.PATCH_PARTNER_MANAGER_DETAILS
            );

            dispatch(setSettingsLanguage(language));
        } catch {}
    };
}

export function PrepareCreatePartner() {
    return partnerManagerSlice.actions.setCreatePartnerPrepared(true);
}

interface PartnerCreationDto {
    billing?: PartnerBillingDto;
    settlement: SettlementDto;
    contractual_contact: PartnerContactApiDto;
    credit_note_recipient?: PartnerContactApiDto;
    salesforce_id: string;
    account_id?: number;
}

interface PartnerBillingDto {
    holder: string;
    iban: string;
    bic: string;
}

interface SettlementDto {
    contractual_wewash_share: number;
    bank_data_available: boolean;
    auto_receive_credit_note: boolean;
    vat_exempted: boolean;
    vat_exemption_reason?: string; // enum
    vat_id?: string;
    contractual_type?: string; // emum
    tenant_id?: number;
    pms_provider?: string; // enum
    country_id: number;
    company_registration_number?: string;
    receive_credit_note_email?: string;
    credit_notes_enabled: boolean;
}

interface PartnerContactApiDto {
    phone?: string;
    name?: string;
    company: string;
    supplementary_line1?: string;
    supplementary_line2?: string;
    supplementary_line3?: string;
    street_and_housenumber: string;
    city: string;
    postal_code: string;
    country: string;
}

export function CreatePartner(
    partner: PartnerCreationDto,
    callback?: (numberOfPartners: number) => void
) {
    return async (dispatch: PbDispatch, getState: PbGetState) => {
        try {
            if (!getState().partnerManager.isCreatePartnerPrepared) {
                console.log('did not prepare create!');
                return;
            }
            if (
                getState().pbRequest[PbRequest.CREATE_PARTNER_REQUEST]
                    .status === RequestStatus.PENDING
            ) {
                console.log('create call still pending');
                return;
            }

            const request = {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(partner),
            };
            await processRequest<void>(
                dispatch,
                `${API}/partners`,
                request,
                PbRequest.CREATE_PARTNER_REQUEST
            );
            dispatch(
                partnerManagerSlice.actions.setCreatePartnerPrepared(false)
            );
            await LoadPartners()(dispatch, getState);

            const numberOfPartners =
                getState().partnerManager.getPartnersResult?.partners?.length ??
                0;
            callback?.(numberOfPartners);
        } catch {}
    };
}

export function LoadPartnerByLocationId(locationId: ReactText) {
    return async (dispatch: PbDispatch) => {
        const result = await processRequest<ManagedPartnerWithLocationDto>(
            dispatch,
            `${API}/locations/${locationId}/partners`,
            {},
            PbRequest.GET_PARTNER_BY_LOCATION_ID
        );
        dispatch(setPartnerByLocationId(result));
    };
}

export function SelectPartnerById(partnerId: ReactText) {
    return async (dispatch: PbDispatch, getState: PbGetState) => {
        const partners =
            getState().partnerManager.getPartnersResult?.partners || [];
        const foundPartner = partners.find(
            (it) => it.external_id === partnerId
        );
        dispatch(setSelectedPartnerId(partnerId));
        dispatch(setSelectedPartner(foundPartner));

        setSelectedAccountingArea(String(partnerId));

        await LoadSpecificPartner(String(partnerId))(dispatch, getState);
    };
}

export default partnerManagerSlice.reducer;
